import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Link, Stack, Typography } from "@mui/material";
import { useGoogleLogin } from "@react-oauth/google";
import {
  Button,
  Checkbox,
  LanguageSelector,
  Notification,
  SocialButton,
  TextField
} from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { useEffect, useState } from "react";
import { useSignIn } from "react-auth-kit";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FcGoogle } from "react-icons/fc";
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams
} from "react-router-dom";
import { signInWithEmailAndPassword, signInWithGoogleCallback } from "../api";
import { ONE_DAY_IN_MINUTES, ONE_WEEK_IN_MINUTES } from "../constants";
import { signInSchema } from "../validation";

export function SignIn() {
  const [loading, setLoading] = useState(false);
  const [googleOauthLoading, setGoogleOauthLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const signIn = useSignIn();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const code = searchParams.get("code");
  const scope = searchParams.get("scope");

  useEffect(() => {
    if (code && scope) {
      setGoogleOauthLoading(true);
      setErrorMessage("");

      signInWithGoogleCallback({ code, scope })
        .then(response => {
          const { result } = response || {};

          if (result?.token) {
            signIn({
              token: result?.token,
              expiresIn: ONE_WEEK_IN_MINUTES,
              tokenType: "Bearer",
              authState: result?.user
            });

            navigate("/churches");
          }
        })
        .catch(error => {
          setErrorMessage(error.errors || error.message);
        })
        .finally(() => {
          setGoogleOauthLoading(false);
        });
    }
  }, [code, scope]);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      email: "",
      password: ""
    },
    resolver: yupResolver(signInSchema)
  });

  const onSubmit = async credentials => {
    setLoading(true);
    setErrorMessage("");

    try {
      const data = await signInWithEmailAndPassword(credentials);

      signIn({
        token: data.result?.token,
        expiresIn: credentials.rememberMe
          ? ONE_WEEK_IN_MINUTES
          : ONE_DAY_IN_MINUTES,
        tokenType: "Bearer",
        authState: data.result?.user
      });

      setLoading(false);

      navigate("/churches");
    } catch (error) {
      setLoading(false);

      setErrorMessage(error.errors || error.message);

      if (error.errors === "Whoops! user is not verified") {
        navigate(`/auth/verification?email=${credentials.email}`);
      }
    }
  };

  const signInWithGoogle = useGoogleLogin({
    flow: "auth-code",
    ux_mode: "redirect",
    redirect_uri: `${window.location.origin}/auth/sign-in`
  });

  return (
    <Box sx={{ my: "auto" }} px={{ xs: 4, md: 6, lg: 12, xl: 22 }}>
      <Notification />
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
        {t("auth.signIn.signIn")}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 4 }}>
        {t("auth.signIn.welcome")}
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <SocialButton
          icon={<FcGoogle size={20} />}
          onClick={() => signInWithGoogle()}
          disabled={googleOauthLoading}
          loading={googleOauthLoading}
          loadingPosition="start"
        >
          {t("auth.common.continueWithGoogle")}
        </SocialButton>
        {/* <SocialButton icon={<BsFacebook color="#039BE5" />}>
          {t("auth.common.signInWithFacebook")}
        </SocialButton> */}
      </Stack>
      <Divider sx={{ my: 2, color: "text.secondary" }}>
        {t("auth.signIn.or")}
      </Divider>
      <Stack spacing={2} component="form" onSubmit={handleSubmit(onSubmit)}>
        <TextField
          label={t("auth.signIn.email")}
          name="email"
          error={!!errors.email?.message}
          helperText={t(errors.email?.message)}
          register={register}
          autoComplete="email"
        />
        <TextField
          label={t("auth.signIn.password")}
          name="password"
          error={!!errors.password?.message}
          helperText={t(errors.password?.message)}
          type="password"
          register={register}
          autoComplete="current-password"
        />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 3 }}
        >
          <Checkbox
            name="rememberMe"
            buttons={[
              {
                checkboxLabel: `${t("auth.signIn.rememberMe")}`,
                value: true,
                id: "remember"
              }
            ]}
            error={!!errors.rememberMe?.message}
            helperText={errors.rememberMe?.message}
            register={register}
          />
          <Link
            component={RouterLink}
            to="/auth/forgot-password"
            variant="body2"
            color="inherit"
            sx={{ textAlign: "right" }}
          >
            {t("auth.signIn.forgotPassword")}
          </Link>
        </Stack>
        <Button
          type="submit"
          size="large"
          fullWidth
          flavor="primary"
          disabled={loading}
        >
          {t("auth.signIn.signIn")}
        </Button>
        <MutationError error={{ errors: errorMessage }} />
      </Stack>
      <Typography variant="body1" sx={{ mt: 1 }}>
        {t("auth.signIn.dontHaveAccount")}{" "}
        <Link component={RouterLink} to="/auth/create-account" color="inherit">
          {t("auth.signIn.createAccount")}
        </Link>
      </Typography>
      <Stack alignItems="center" mt={4}>
        <LanguageSelector />
      </Stack>
    </Box>
  );
}
